import axios from 'axios';
import decode from 'jwt-decode';
// eslint-disable-next-line import/extensions
import Cookies from 'js-cookie';
import { LOGIN, LOGOUT, FIRST_AUTH_DATA } from './types';
import api from '../../../utils/api';
import getTokens from '../../../utils/getTokens';
import sessionStorageHelper from '../../../utils/sessionStorageHelper';

const UserTokens = getTokens();

// In-flight deduplication + completed-fetch cache.
// Keys in use:
//   'companySettings:{type}' — companySettingsSelects(type)
const _pendingActions = {};
const _completedActions = {};

export default {
  async userLogin({ commit }, userObj) {
    const { data } = await axios(api.auth.signIn(userObj));
    Cookies.set(
      'userTokens',
      JSON.stringify({
        access: data.access,
        refresh: data.refresh,
      }),
    );
    commit(LOGIN, data);
  },
  async userLogout({ commit, dispatch }) {
    try {
      Cookies.remove('userTokens');
      sessionStorageHelper.removeItem('userFirstAuth');
      dispatch('aiChat/reset', null, { root: true });
      dispatch('user/reset', null, { root: true });
      commit(LOGOUT);
    } catch (e) {
      console.warn(e);
    }
  },
  async userRegister({}, userObj) {
    await axios(api.auth.signUp(userObj));
  },
  async becomeMember({}, userObj) {
    await axios(api.auth.becomeMember(userObj));
  },
  async setPassword({ commit }, data) {
    const firstAuthData = await axios(api.auth.setPassword(data));
    const password = data.password || (data.passwords && data.passwords.password);
    if (firstAuthData && firstAuthData.data && firstAuthData.data.username && password) {
      try {
        const response = await axios(
          api.auth.signIn({ username: firstAuthData.data.username, password }),
        );
        const customerId = response.data.access
          ? decode(response.data.access).customer_id
          : firstAuthData.data.user_id;
        const userInfo = await axios(api.user.fetch(
          customerId, response.data.access,
        ));
        commit(FIRST_AUTH_DATA, {
          data: {
            customerId,
            email: firstAuthData.data.username,
            adminCompany: userInfo.data.admin_company,
            isFirstLogin: userInfo.data.is_first_login,
            access: response.data.access,
            refresh: response.data.refresh,
          },
        });
      } catch (err) {
        console.warn('Failed to complete first auth login sequence:', err);
      }
    }
    return firstAuthData;
  },
  async resetPassword({}, data) {
    return await axios(api.auth.setPassword(data));
  },
  async resendToken({}, data) {
    await axios(api.auth.resendToken(data));
  },
  async userRefresh({ commit, dispatch }) {
    try {
      const response = await axios(api.auth.refresh());
      const userTokens = {
        access: response.data.access,
        refresh: UserTokens ? JSON.parse(UserTokens).refresh : null,
      };
      Cookies.set('userTokens', JSON.stringify(userTokens));
      commit(LOGIN, userTokens);
    } catch (e) {
      console.warn(e);
      dispatch('userLogout');
      Cookies.remove('userTokens');
    }
  },
  async resetPasswordHashVerify({}, hash) {
    await axios(api.auth.verifyResetPasswordHash(hash));
  },
  async addUserFirstSettings({}, data) {
    await axios(api.auth.addUserFirstSettings(data));
  },
  async addCompanyFirstSettings({}, data) {
    await axios(api.auth.addCompanyFirstSettings(data));
  },
  async addCompanyType({}, data) {
    await axios(api.auth.addCompanyType(data));
  },
  firstLogin({ commit }, userObj) {
    const tokens = {
      access: userObj.access,
      refresh: userObj.refresh,
    };
    Cookies.set(
      'userTokens',
      JSON.stringify(tokens),
    );
    sessionStorageHelper.removeItem('userFirstAuth');
    commit(FIRST_AUTH_DATA, { data: {} });
    commit(LOGIN, tokens);
  },
  async companySettingsSelects(_, type) {
    const key = `companySettings:${type}`;
    if (_completedActions[key]) return _completedActions[key];
    if (_pendingActions[key]) return _pendingActions[key];
    _pendingActions[key] = (async () => {
      try {
        const { data } = await axios(api.auth.companySettingsSelects(type));
        _completedActions[key] = data.results;
        return data.results;
      } finally {
        delete _pendingActions[key];
      }
    })();
    return _pendingActions[key];
  },
};
